import { useState } from 'react'

import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FieldLabel } from '@cash-design-system/react'
import { PasswordVerificationBlocker } from '@cash-web/protos/squareup/franklin/blockers/password_blockers.pb'
import { useMutationVerifyPassword } from '@cash-web/shared-blocker-data-access-api'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationSubmitForm } from '@cash-web/shared-data-access-api/react-query/useMutationSubmitForm'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { FormInput, PasswordInput, PasswordInputLegacy } from '@cash-web/shared-feature-form'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { BaseBlocker, SubmitButton } from '../BaseBlocker/BaseBlocker'
import { BlockerLoading } from '../BlockerLoading/BlockerLoading'

type PasswordVerificationForm = {
  'current-password': string
}

type BlockerPasswordVerificationProps = {
  blocker: PasswordVerificationBlocker
}

export const BlockerPasswordVerification = ({ blocker }: BlockerPasswordVerificationProps) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const flow = useFlowContext()
  const { handleBlockerResponse } = useBlockerResponseHandler()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const { mutateAsync: submitForm } = useMutationSubmitForm({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
    },
  })
  const { mutateAsync: verifyPassword } = useMutationVerifyPassword({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
    },
  })

  const form = useForm<PasswordVerificationForm>({
    defaultValues: {
      'current-password': '',
    },
  })

  const password = useWatch({ name: 'current-password', control: form.control })

  const handleSubmit = async (values: PasswordVerificationForm) => {
    await handleBlockerResponse({
      isResponseSuccess: response => response.response_context?.dialog_message == null,
      promisedBlockerUpdate: verifyPassword({ password: values['current-password'] }),
    })
  }

  if (isLoading) {
    return <BlockerLoading />
  }

  return (
    <BaseBlocker
      mainText={t('Enter your password for Cash App Taxes', { context: 'title' })}
      secondaryText={t('You created this password when you started using Cash App Taxes.', {
        context: 'subtitle',
      })}
      footer={null}
      onSubmit={handleSubmit}
      form={form}
      buttons={[<SubmitButton key="submit-password-btn" disabled={!password} />]}
      helpModal={{
        tooltip: t('Forgot password?', { context: 'help option' }),
        // TODO: remove once server updates this
        helpModalStart: async () => {
          setIsLoading(true)

          flow.updateWithResponse({
            response: submitForm({
              action_id: blocker.help_action?.menu_action?.actions?.[0].submit_action?.id,
            }),
            queriesToInvalidate: [],
          })

          setIsLoading(false)
        },
      }}
    >
      {isArcadeMigrationEnabled ? (
        <PasswordInput autoFocus={true} />
      ) : (
        <FieldLabel label={t('Password', { context: 'label' })}>
          <FormInput
            autoFocus={true}
            customInput={PasswordInputLegacy}
            placeholder={t('Enter password', { context: 'placeholder' })}
            name="current-password"
            autoComplete="current-password"
            isInvalid={Boolean(flow.error)}
          />
        </FieldLabel>
      )}
    </BaseBlocker>
  )
}
